import { useMemo, useState, useEffect, useCallback } from "react";
import { IItemProps } from "devextreme-react/form";

import { ILogger } from "@ezhou/common";
import { PageProps } from "@ezhou/core";

import { SaveChangeResult } from "../../components/dataGridActionUtil";
import { CustomerOrganization } from "../../model/apiModel/systemadminmodel";

import { CrudDataGrid } from "../common/CrudDataGrid";

import { CustomerModel } from "./model/model";
import { dummyCustomerOrganizationRow } from "./model/metaData";

type OrganizationOption = {
  organizationId: number;
  fullName: string;
};

export type CustomerOrganizationsProps = {
  masterData: CustomerModel;
  pageProps: PageProps;
  logger: ILogger;
};

export const CustomerOrganizations = (props: CustomerOrganizationsProps) => {
  const [allOrganizations, setAllOrganizations] = useState<OrganizationOption[]>([]);
  const [customerOrganizations, setCustomerOrganizations] = useState<CustomerOrganization[]>([]);
  const [availableOrganizations, setAvailableOrganizations] = useState<OrganizationOption[]>([]);

  const excludedColumns = useMemo(() => new Set<string>(["customerId"]), []);

  useEffect(() => {
    const fetchAllOrganizations = async () => {
      try {
        const response = await props.pageProps.appUtil.httpClients["eAccountingService"].get<OrganizationOption[]>(
          `/organizations/get-all`,
          (err) => {
            props.logger.error("Failed to fetch available organizations", err);
          },
        );
        if (response) {
          setAllOrganizations(response);
        }
      } catch (error) {
        props.logger.error("Error fetching available organizations", error);
      }
    };
    fetchAllOrganizations();
  }, [props.masterData.customerId, props.pageProps.appUtil.httpClients, props.logger]);

  useEffect(() => {
    // Calculate available organizations by excluding already-linked organizations
    const linkedIds = new Set(customerOrganizations.map((co) => co.organizationId));
    const available = allOrganizations.filter((org) => !linkedIds.has(org.organizationId));
    setAvailableOrganizations(available);
  }, [allOrganizations, customerOrganizations]);

  const refreshData = useCallback(async () => {
    const response = await props.pageProps.appUtil.httpClients["eAccountingService"].get<CustomerOrganization[]>(
      `/customers/get-organizations/${props.masterData.customerId}`,
      (err) => {
        throw err;
      },
    );
    if (response) {
      setCustomerOrganizations(response);
      return response;
    }
    var ret: CustomerOrganization[] = [];
    setCustomerOrganizations(ret);
    return ret;
  }, [props.masterData.customerId, props.pageProps.appUtil.httpClients]);

  const filterRowDataForForm = (data: any) => {
    return { organizationId: data.organizationId };
  };

  const editorItemOptions = useMemo(
    () =>
      new Map<string, IItemProps>([
        [
          "organizationId",
          {
            editorType: "dxSelectBox",
            editorOptions: {
              dataSource: availableOrganizations,
              displayExpr: "fullName",
              valueExpr: "organizationId",
              searchEnabled: true,
              placeholder: "Select an organization",
            },
          },
        ],
      ]),
    [availableOrganizations],
  );

  const sendChange = async (change: {
    type: any;
    data: any;
    key: any;
  }): Promise<SaveChangeResult> => {
    switch (change.type) {
      case "insert":
        const response = await props.pageProps.appUtil.httpClients["eAccountingService"].post(
          `customers/add-organization`,
          { customerId: props.masterData.customerId, ...change.data },
          (err: any) => {
            throw err;
          },
        );
        if (response) {
          return {
            data: {
              customerId: props.masterData.customerId,
              organizationName:
                allOrganizations.find((o) => o.organizationId === change.data.organizationId)?.fullName ?? "Unknown",
              ...change.data,
            },
            keyExpr: "organizationId",
          };
        }
        break;
      case "update":
        break;
      case "remove":
        const responseRemove = await props.pageProps.appUtil.httpClients["eAccountingService"].post(
          `customers/remove-organization`,
          { customerId: props.masterData.customerId, organizationId: change.key },
          (err: any) => {
            throw err;
          },
        );
        break;
      default:
        break;
    }
    return {
      data: change.data,
      keyExpr: "organizationId",
    };
  };

  return (
    <>
      <div>Organizations</div>
      <CrudDataGrid
        pageProps={props.pageProps}
        logger={props.logger}
        dummyRow={dummyCustomerOrganizationRow}
        keyExpr={"organizationId"}
        formTitle="Add Organization"
        formColCount={2}
        formColSpan={2}
        refreshData={refreshData}
        allowAdding={true}
        allowUpdating={false}
        allowDeleting={true}
        filterRowDataForForm={filterRowDataForForm}
        editorItemOptions={editorItemOptions}
        sendChange={sendChange}
        hideColumnChooser={true}
        hideSearchPanel={true}
        excludedColumns={excludedColumns}
      ></CrudDataGrid>
    </>
  );
};
